import type { SectionRow, StoryMoment } from '@/types'

import { Heart } from 'lucide-react'
import { cn } from '@/lib/utils'

import Photo from '@/components/landing/photo'
import Reveal from '@/components/landing/reveal'
import SectionHeading from '@/components/landing/section-heading'

interface StorySectionProps {
    section?: SectionRow
    moments: Array<StoryMoment>
}

const TILTS = ['-rotate-2', 'rotate-2', '-rotate-1', 'rotate-1', '-rotate-2']

export default function StorySection({ section, moments }: StorySectionProps) {
    return (
        <section id='story' className='relative overflow-hidden bg-cream py-24 sm:py-32'>
            <div aria-hidden className='absolute top-24 -left-24 size-72 rounded-full bg-blush blur-3xl' />
            <div aria-hidden className='absolute -right-24 bottom-24 size-72 rounded-full bg-[#ffe9d6] blur-3xl' />

            <div className='relative mx-auto max-w-6xl px-6 lg:px-10'>
                <SectionHeading section={section} />

                <div className='relative mt-16 sm:mt-20'>
                    <span aria-hidden className='absolute top-2 bottom-2 left-5 w-px bg-gradient-to-b from-petal/0 via-petal/40 to-petal/0 md:left-1/2' />

                    <ol className='relative'>
                        {moments.map((moment, index) => {
                            const flipped = index % 2 === 1

                            return (
                                <li key={moment.id} className='relative pb-16 pl-14 last:pb-0 md:pl-0'>
                                    <div className='relative grid items-center gap-8 md:grid-cols-2 md:gap-20'>
                                        <span aria-hidden className='absolute top-6 -left-9 z-10 grid size-10 -translate-x-1/2 place-items-center rounded-full bg-white shadow-md ring-4 ring-cream md:top-1/2 md:left-1/2 md:-translate-y-1/2'>
                                            <Heart className='size-4 fill-petal text-petal' />
                                        </span>

                                        {moment.photo && (
                                            <Reveal
                                                direction={flipped ? 'right' : 'left'}
                                                className={cn(flipped && 'md:order-2')}
                                            >
                                                <div className={cn(
                                                    'mx-auto max-w-sm rounded-[1.75rem] bg-white p-3 shadow-[0_24px_60px_-28px_rgba(91,21,48,0.45)] transition-transform duration-500 hover:rotate-0',
                                                    TILTS[index % TILTS.length]
                                                )}>
                                                    <Photo
                                                        src={moment.photo}
                                                        alt={moment.photo_alt ?? ''}
                                                        rotation={moment.photo_rotation}
                                                        className='aspect-[4/5] rounded-[1.25rem] bg-blush'
                                                    />
                                                </div>
                                            </Reveal>
                                        )}

                                        <Reveal
                                            direction={flipped ? 'left' : 'right'}
                                            delay={120}
                                            className={cn('md:max-w-md', flipped && 'md:order-1 md:ml-auto md:text-right')}
                                        >
                                            {moment.chapter_label && (
                                                <p className='text-xs font-bold tracking-[0.3em] text-petal-deep uppercase'>
                                                    <span data-mdlfy-cms={`story_moments:${moment.id}:chapter_label`}>
                                                        {moment.chapter_label}
                                                    </span>
                                                </p>
                                            )}

                                            {moment.title && (
                                                <h3
                                                    data-mdlfy-cms={`story_moments:${moment.id}:title`}
                                                    className='mt-3 font-heading text-3xl font-semibold text-wine sm:text-4xl'
                                                >
                                                    {moment.title}
                                                </h3>
                                            )}

                                            {moment.caption && (
                                                <p
                                                    data-mdlfy-cms={`story_moments:${moment.id}:caption`}
                                                    className='mt-4 font-hand text-2xl leading-snug text-ink/80 sm:text-[1.7rem]'
                                                >
                                                    {moment.caption}
                                                </p>
                                            )}
                                        </Reveal>
                                    </div>
                                </li>
                            )
                        })}
                    </ol>
                </div>
            </div>
        </section>
    )
}
